import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AdminGetRoutesResDto } from "service/api/estabrookSchemas";
import type { TimeAdjustments } from "./use-realtime-time-adjustments";

/** Keep the existing per-row reporting flow; reset before passive row reports. */
export const useRoutePlannerAdjustments = (data: AdminGetRoutesResDto[]) => {
  // Aggregate adjustments from all routes for the hovered address tooltip
  const [allAdjustmentsMap, setAllAdjustmentsMap] = useState<
    Record<string, number>
  >({});
  const [
    allAdjustedEstimatedTimeMinutesMap,
    setAllAdjustedEstimatedTimeMinutesMap,
  ] = useState<Record<string, number>>({});

  // Build a stable signature for the current table dataset.
  // We use this to clear stale real-time adjustment state when switching
  // dates/routes so old values from a previous view cannot leak in.
  const routeLocationsSignature = useMemo(
    () =>
      data
        .map((route) => {
          const locationIds =
            route.locations?.map((location) => location.locationId).join(",") ??
            "";
          return `${route.id}:${locationIds}`;
        })
        .join("|"),
    [data],
  );

  useLayoutEffect(() => {
    setAllAdjustmentsMap({});
    setAllAdjustedEstimatedTimeMinutesMap({});
  }, [routeLocationsSignature]);

  // Keep a quick lookup of location ids by route so we can clear stale
  // real-time adjustment values route-by-route on every update.
  const routeLocationIdsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    data.forEach((route) => {
      map[route.id] =
        route.locations?.map((location) => location.locationId) ?? [];
    });
    return map;
  }, [data]);

  // Callback to receive adjustments from OrderLayout components
  const handleAdjustmentsChange = useCallback(
    (
      routeId: string,
      adjustments: Record<
        string,
        { adjustedServiceTime?: number; adjustedEstimatedTimeMinutes?: number }
      >,
    ) => {
      setAllAdjustmentsMap((prev) => {
        const updated = { ...prev };
        // Clear previous values for this route first to avoid stale leakage.
        const routeLocationIds = routeLocationIdsMap[routeId] ?? [];
        routeLocationIds.forEach((locationId) => {
          delete updated[locationId];
        });

        // Apply current adjustments for locations in this route.
        Object.entries(adjustments).forEach(([locationId, adjustment]) => {
          if (adjustment.adjustedServiceTime !== undefined) {
            updated[locationId] = adjustment.adjustedServiceTime;
          }
        });
        return updated;
      });

      setAllAdjustedEstimatedTimeMinutesMap((prev) => {
        const updated = { ...prev };
        // Clear previous values for this route first to avoid stale leakage.
        const routeLocationIds = routeLocationIdsMap[routeId] ?? [];
        routeLocationIds.forEach((locationId) => {
          delete updated[locationId];
        });

        // Apply current adjusted ETA values for locations in this route.
        Object.entries(adjustments).forEach(([locationId, adjustment]) => {
          if (adjustment.adjustedEstimatedTimeMinutes !== undefined) {
            updated[locationId] = adjustment.adjustedEstimatedTimeMinutes;
          }
        });
        return updated;
      });
    },
    [routeLocationIdsMap],
  );

  return {
    allAdjustmentsMap,
    allAdjustedEstimatedTimeMinutesMap,
    handleAdjustmentsChange,
  };
};

/** Re-send unchanged values when a new dataset supplies a new receiver. */
export const useReportRouteAdjustments = (
  routeId: string,
  adjustments: Record<string, TimeAdjustments>,
  onAdjustmentsChange?: (
    routeId: string,
    adjustments: Record<string, TimeAdjustments>,
  ) => void,
) => {
  const previousReport = useRef<{
    routeId: string;
    json: string;
    receiver: typeof onAdjustmentsChange;
  }>();

  useEffect(() => {
    if (!onAdjustmentsChange) return;
    const json = JSON.stringify(adjustments);
    const previous = previousReport.current;
    if (
      previous?.json !== json ||
      previous.routeId !== routeId ||
      previous.receiver !== onAdjustmentsChange
    ) {
      previousReport.current = { routeId, json, receiver: onAdjustmentsChange };
      onAdjustmentsChange(routeId, adjustments);
    }
  }, [adjustments, routeId, onAdjustmentsChange]);
};
